import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { StepProgressIndicator, ProgressStep } from '../components/StepProgressIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_LEAVE_HINT, WIZARD_PATHS, backUrlFor, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { waitForSessionUpdate } from '../lib/navigationHelpers';
import { Sparkles, CreditCard as Edit3, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import { logGenerationError } from '../lib/errorLogger';
import { useCreditPricing } from '../hooks/useCreditPricing';

const IMAGE_GENERATION_TIMEOUT = 90000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  const { timeout, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function ImageGenerate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, currentIdea } = useSession();
  const { isRegeneration, customPrompt: initialCustomPrompt, forceCustomPrompt } = (location.state ?? {}) as any;
  const sessionId = sessionIdFromLocation(location);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { pricing } = useCreditPricing();
  const imageCost = pricing['images_generate'] ?? 400;
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [dashboardIdea, setDashboardIdea] = useState<any>(null);
  const [generationInfo, setGenerationInfo] = useState<{
    current: number;
    max: number;
    remaining: number;
  } | null>(null);
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState(initialCustomPrompt || '');
  const [useCustom, setUseCustom] = useState(!!forceCustomPrompt);
  const [uploadMode, setUploadMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { label: 'Idee analyseren', status: 'pending' },
    { label: 'Afbeelding prompts maken', status: 'pending' },
    { label: 'Afbeelding genereren', status: 'pending' },
    { label: 'Afbeeldingen ophalen', status: 'pending' },
    { label: 'Afbeeldingen opslaan', status: 'pending' },
  ]);
  const hasAutoGeneratedRef = React.useRef(false);
  const isMountedRef = useRef(true);

  const updateStepStatus = (stepIndex: number, status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    setProgressSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status } : step
    ));
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Zonder sessie-id (direct via URL geopend) is er niets te genereren.
  useEffect(() => {
    if (!sessionId) {
      navigate(WIZARD_EXIT_ROUTE, { replace: true });
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sid: string) => {
    try {
      setLoading(true);

      const { data: sessionData } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sid)
        .maybeSingle();

      if (sessionData) {
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', sessionData.user_id)
          .maybeSingle();

        const { data: ideaData } = await supabase
          .from('blog_ideas')
          .select('*')
          .eq('id', sessionData.approved_idea_id)
          .maybeSingle();

        setDashboardSession(sessionData);
        setDashboardUser(userData);
        setDashboardIdea(ideaData);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const activeUser = dashboardUser || user;
  const activeIdea = dashboardIdea || currentIdea;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const backUrl = backUrlFor(WIZARD_PATHS.imageGenerate, activeSession?.id);

  const handleGenerateImages = async () => {
    if (!activeSession || !activeUser) return;
    if (isGenerating) return;

    setIsGenerating(true);
    try {
      console.log('Checking for existing images...');
      updateStepStatus(0, 'in_progress');

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('images, article_content, selected_title, selected_intro')
        .eq('id', activeSession.id)
        .maybeSingle();

      if (!sessionData?.article_content) {
        console.error('No article content found in session');
        updateStepStatus(0, 'error');
        throw new Error('Er is geen artikel beschikbaar');
      }

      if (!sessionData?.selected_title) {
        console.error('No title selected');
        updateStepStatus(0, 'error');
        throw new Error('Er is geen titel geselecteerd');
      }

      if (!sessionData?.selected_intro) {
        console.error('No intro selected');
        updateStepStatus(0, 'error');
        throw new Error('Er is geen intro geselecteerd');
      }

      if (sessionError) {
        console.error('Error fetching session:', sessionError);
      }

      const inflightKey = `gen_inflight_images_${activeSession.id}`;
      if (!isRegeneration) {
        const inflightSince = Number(sessionStorage.getItem(inflightKey) || 0);
        if (inflightSince && Date.now() - inflightSince < 3 * 60 * 1000) {
          // Een eerdere aanroep loopt waarschijnlijk nog (terug, Sluiten of refresh tijdens het schrijven):
          // wacht op het resultaat in plaats van opnieuw te betalen.
          console.log('[ImageGenerate] Generation already in flight, waiting for result...');
          updateStepStatus(0, 'completed');
          updateStepStatus(1, 'completed');
          updateStepStatus(2, 'in_progress');
          const arrived = await waitForSessionUpdate(activeSession.id, 'images', 45, 2000);
          if (arrived) {
            sessionStorage.removeItem(inflightKey);
            if (!isMountedRef.current) return;
            navigate(wizardUrl(WIZARD_PATHS.selectImage, activeSession.id), { replace: true });
            return;
          }
          sessionStorage.removeItem(inflightKey);
        }
      }
      sessionStorage.setItem(inflightKey, String(Date.now()));

      updateStepStatus(0, 'completed');
      updateStepStatus(1, 'in_progress');

      console.log('Generating new images...');

      updateStepStatus(1, 'completed');
      updateStepStatus(2, 'in_progress');

      const requestBody: any = {
        session_id: activeSession.id,
        user_id: activeUser.id,
        idea_title: activeIdea?.idea_title || '',
        idea_content: activeIdea?.idea_content || '',
      };

      if (useCustom && customPrompt.trim()) {
        requestBody.custom_prompt = customPrompt.trim();
      }

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      let lastError: Error | null = null;
      let imagesData: any = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          setRetryAttempt(attempt);
          console.log(`[ImageGenerate] Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`);

          const imagesResponse = await fetchWithTimeout(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-generate`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
              timeout: IMAGE_GENERATION_TIMEOUT,
            }
          );

          imagesData = await imagesResponse.json();

          if (!imagesResponse.ok) {
            console.error(`[ImageGenerate] API error on attempt ${attempt}:`, imagesData);
            throw new Error(imagesData.error || 'Failed to generate images');
          }

          console.log(`[ImageGenerate] Success on attempt ${attempt}`);
          lastError = null;
          break;
        } catch (error) {
          lastError = error as Error;
          console.error(`[ImageGenerate] Error on attempt ${attempt}:`, error);

          const errorMessage = error instanceof Error ? error.message : String(error);
          const isTimeout = errorMessage.includes('Timeout');
          const isNetwork = errorMessage.includes('fetch') || errorMessage.includes('network');

          await logGenerationError({
            generationType: 'blog_image',
            errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
            errorMessage: errorMessage,
            technicalError: error instanceof Error ? error.stack : undefined,
            retryAttempt: attempt - 1,
            sessionId: activeSession.id,
            additionalContext: {
              service: 'leonardo',
              apiType: 'image_generation',
              timeout: IMAGE_GENERATION_TIMEOUT,
              lastError: errorMessage,
              prompt: requestBody.custom_prompt || undefined,
              maxRetries: MAX_RETRY_ATTEMPTS,
              attemptNumber: attempt,
            },
          });

          if (attempt < MAX_RETRY_ATTEMPTS) {
            console.log(`[ImageGenerate] Waiting ${RETRY_DELAY}ms before retry...`);
            await sleep(RETRY_DELAY);
          }
        }
      }

      if (lastError) {
        updateStepStatus(3, 'error');

        await logGenerationError({
          generationType: 'blog_image',
          errorCategory: 'TIMEOUT',
          errorMessage: `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt`,
          technicalError: lastError.stack,
          retryAttempt: MAX_RETRY_ATTEMPTS,
          sessionId: activeSession.id,
          additionalContext: {
            service: 'leonardo',
            apiType: 'image_generation',
            timeout: IMAGE_GENERATION_TIMEOUT,
            lastError: lastError.message,
            prompt: requestBody.custom_prompt || undefined,
            maxRetries: MAX_RETRY_ATTEMPTS,
          },
        });

        throw new Error(
          `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt. Probeer het later opnieuw of neem contact op met support.`
        );
      }

      if (!imagesData) {
        updateStepStatus(3, 'error');
        throw new Error('Geen resultaat ontvangen van AI');
      }

      setRetryAttempt(0);
      updateStepStatus(2, 'completed');
      updateStepStatus(3, 'in_progress');

        console.log('Images generated:', imagesData.image_urls);
        updateStepStatus(3, 'completed');
        updateStepStatus(4, 'in_progress');

        const { error: updateError } = await supabase
          .from('sessions')
          .update({ images: imagesData.image_urls || [] })
          .eq('id', activeSession.id);

        if (updateError) {
          console.error('Error saving images:', updateError);
          updateStepStatus(4, 'error');
          throw new Error('Fout bij opslaan van afbeeldingen');
        }

        sessionStorage.removeItem(inflightKey);

        console.log('Verifying images were saved to database...');
        const verified = await waitForSessionUpdate(activeSession.id, 'images', 15, 300);

        if (!verified) {
          console.warn('Could not verify images in database within timeout, but continuing anyway');
          console.log('Images that were returned:', imagesData.image_urls);
        }

        updateStepStatus(4, 'completed');
        console.log('Images verified in database!');

        await new Promise(resolve => setTimeout(resolve, 500));

        if (!isMountedRef.current) return;
        navigate(wizardUrl(WIZARD_PATHS.selectImage, activeSession.id), { replace: true });
        setIsGenerating(false);
      } catch (err) {
        sessionStorage.removeItem(`gen_inflight_images_${activeSession.id}`);
        console.error('Error generating images:', err);

        await logGenerationError({
          generationType: 'blog_image',
          errorCategory: 'UNKNOWN',
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
          technicalError: err instanceof Error ? err.stack : undefined,
          retryAttempt: retryAttempt,
          sessionId: activeSession?.id,
          additionalContext: {
            service: 'leonardo',
            apiType: 'image_generation',
            timeout: IMAGE_GENERATION_TIMEOUT,
            lastError: err instanceof Error ? err.message : String(err),
          },
        });

        setError(
          `Er ging iets mis bij het genereren van afbeeldingen: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
        setIsGenerating(false);
        setRetryAttempt(0);
      }
  };

  useEffect(() => {
    if (loading) return;
    if (!activeSession || !activeUser) return;
    if (isGenerating) return;

    const fetchData = async () => {
      const { data: sessionData } = await supabase
        .from('sessions')
        .select('images, image_generation_count')
        .eq('id', activeSession.id)
        .maybeSingle();

      const { data: configData } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'max_image_generations')
        .maybeSingle();

      const currentCount = sessionData?.image_generation_count || 0;
      const maxGenerations = parseInt(configData?.value || '3');

      setGenerationInfo({
        current: currentCount,
        max: maxGenerations,
        remaining: maxGenerations - currentCount,
      });

      const hasImagesAlready = !!(sessionData?.images && Array.isArray(sessionData.images) && sessionData.images.length > 0);

      // Mount tijdens een generatie die de vorige keer nog liep (terug, Sluiten of refresh):
      // wacht het resultaat af in plaats van de keuze te tonen (of erger, opnieuw te genereren).
      const inflightKey = `gen_inflight_images_${activeSession.id}`;
      const inflightSince = Number(sessionStorage.getItem(inflightKey) || 0);
      if (!isRegeneration && inflightSince && Date.now() - inflightSince < 3 * 60 * 1000 && !hasImagesAlready) {
        console.log('[ImageGenerate] Generation already in flight (mount), waiting for result...');
        setIsGenerating(true);
        updateStepStatus(0, 'completed');
        updateStepStatus(1, 'completed');
        updateStepStatus(2, 'in_progress');
        const arrived = await waitForSessionUpdate(activeSession.id, 'images', 45, 2000);
        sessionStorage.removeItem(inflightKey);
        if (arrived) {
          if (!isMountedRef.current) return;
          navigate(wizardUrl(WIZARD_PATHS.selectImage, activeSession.id), { replace: true });
          return;
        }
        setIsGenerating(false);
        return;
      }

      if (!isRegeneration && hasImagesAlready) {
        console.log('Using existing images:', sessionData.images);
        if (!isMountedRef.current) return;
        navigate(wizardUrl(WIZARD_PATHS.selectImage, activeSession.id), { replace: true });
      }
    };

    fetchData();
  }, [loading, activeSession?.id, activeUser?.id, isGenerating, isRegeneration, navigate]);

  React.useEffect(() => {
    if (forceCustomPrompt && initialCustomPrompt) {
      setUseCustom(true);
      setCustomPrompt(initialCustomPrompt);
    }
  }, [forceCustomPrompt, initialCustomPrompt]);

  React.useEffect(() => {
    // Eén keer per history-entry: re-entry op dezelfde entry (bijv. na Terug/Vooruit)
    // mag niet nog een keer automatisch credits uitgeven.
    const autoKey = `image_auto_${location.key}`;
    if (
      forceCustomPrompt &&
      initialCustomPrompt &&
      !loading &&
      !isGenerating &&
      !hasAutoGeneratedRef.current &&
      !sessionStorage.getItem(autoKey) &&
      activeSession &&
      activeUser
    ) {
      console.log('[ImageGenerate] Auto-starting generation with custom prompt');
      hasAutoGeneratedRef.current = true;
      sessionStorage.setItem(autoKey, '1');
      handleGenerateImages();
    }
  }, [forceCustomPrompt, initialCustomPrompt, loading, isGenerating, activeSession, activeUser, location.key]);

  if (!sessionId) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Afbeeldingen genereren"
          onBack={() => { if (backUrl) navigate(backUrl, { replace: true }); }}
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => { if (backUrl) navigate(backUrl, { replace: true }); }}
              className="btn-secondary mt-4"
            >
              Terug naar intro's
            </button>
          </div>
        </main>
      </div>
    );
  }

  const steps = getWizardSteps(activeSession, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Afbeeldingen genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Afbeeldingen genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <StepIndicator steps={steps} interactive={false} />
          <div className="space-y-6 mt-6">
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
                We maken afbeeldingen voor je...
              </h1>
              <p className="text-gray-600">Een moment geduld.</p>
              <p className="text-sm text-gray-500">{WIZARD_LEAVE_HINT}</p>
            </div>
            <StepProgressIndicator steps={progressSteps} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Afbeeldingen genereren"
        onBack={() => { if (backUrl) navigate(backUrl); }}
        onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <StepIndicator steps={steps} onStepClick={handleStepClick} />
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
            {isRegeneration ? 'Nieuwe afbeeldingen genereren' : 'Klaar om afbeeldingen te genereren'}
          </h1>
          {forceCustomPrompt ? (
            <p className="text-gray-600">
              Je gebruikt een eigen prompt omdat het maximum aantal automatische generaties is bereikt
            </p>
          ) : (
            <p className="text-gray-600">
              Kies tussen automatische AI-optimalisatie of gebruik je eigen prompt
            </p>
          )}
          {!forceCustomPrompt && (
            <p className="text-sm text-gray-500">
              Elke generatie kost {imageCost} credits en levert 4 afbeeldingen op om uit te kiezen.
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div className="card p-6 space-y-4">
            {!forceCustomPrompt && (
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="auto"
                  name="prompt-mode"
                  checked={!useCustom}
                  onChange={() => setUseCustom(false)}
                  className="w-4 h-4 text-[#231F20] focus:ring-[#231F20]"
                />
                <label htmlFor="auto" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Sparkles className="w-5 h-5 text-[#231F20]" />
                  <div>
                    <div className="font-semibold text-[#231F20]">Automatisch (aanbevolen)</div>
                    <div className="text-sm text-gray-600">
                      AI analyseert je artikel en genereert een geoptimaliseerde prompt
                    </div>
                  </div>
                </label>
              </div>
            )}

            {!forceCustomPrompt && (
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="custom"
                  name="prompt-mode"
                  checked={useCustom}
                  onChange={() => setUseCustom(true)}
                  className="w-4 h-4 text-[#231F20] focus:ring-[#231F20]"
                />
                <label htmlFor="custom" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Edit3 className="w-5 h-5 text-[#231F20]" />
                  <div>
                    <div className="font-semibold text-[#231F20]">Eigen prompt</div>
                    <div className="text-sm text-gray-600">
                      Omschrijf zelf hoe de afbeelding eruit moet komen te zien
                    </div>
                  </div>
                </label>
              </div>
            )}

            {useCustom && (
              <div className={`mt-4 space-y-3 ${forceCustomPrompt ? '' : 'pl-7'}`}>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Vrouw van 50 zit in een koffietent te typen op haar Macbook, natuurlijk licht van zijraam, gefotografeerd over haar schouder, gezicht niet zichtbaar, notitieblok en telefoon op tafel, wazige achtergrond met café-interieur, warme kleuren"
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent resize-none"
                />
                <div className="flex items-start justify-between text-sm">
                  <div className="text-gray-500">
                    {customPrompt.length} karakters
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                    className="flex items-center gap-1 text-[#231F20] hover:underline"
                  >
                    {showCustomPrompt ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Verberg tips
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Toon tips
                      </>
                    )}
                  </button>
                </div>

                {showCustomPrompt && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-2">
                    <div className="font-semibold text-blue-900">Tips voor effectieve prompts:</div>
                    <ul className="space-y-1 text-blue-800 list-disc list-inside">
                      <li>Beschrijf specifieke onderwerpen en scènes</li>
                      <li>Voeg compositie details toe (bijv. close-up, overhead angle)</li>
                      <li>Specificeer belichting (natural light, soft lighting, etc.)</li>
                      <li>Gebruik stijl keywords (professional, candid, corporate, modern)</li>
                      <li>Vermijd tekst in de afbeelding</li>
                      <li>Denk aan: wie, wat, waar, hoe het eruitziet</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {!uploadMode ? (
            <>
              <button
                onClick={handleGenerateImages}
                disabled={useCustom && !customPrompt.trim()}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Genereer afbeeldingen ({imageCost} credits)
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">of</span>
                </div>
              </div>

              <button
                onClick={() => setUploadMode(true)}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload eigen afbeelding
              </button>
            </>
          ) : (
            <div className="card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#231F20]">Upload eigen afbeelding</h3>
                <button
                  onClick={() => {
                    setUploadMode(false);
                    setSelectedFile(null);
                    setUploadPreview(null);
                  }}
                  className="text-sm text-gray-600 hover:text-[#231F20]"
                >
                  Annuleren
                </button>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#231F20] transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-upload')?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-[#231F20]', 'bg-gray-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-[#231F20]', 'bg-gray-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-[#231F20]', 'bg-gray-50');
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith('image/')) {
                    handleFileSelect(file);
                  }
                }}
              >
                {uploadPreview ? (
                  <div className="space-y-4">
                    <img
                      src={uploadPreview}
                      alt="Preview"
                      className="max-h-64 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-gray-600">
                      {selectedFile?.name} ({(selectedFile?.size! / 1024 / 1024).toFixed(2)} MB)
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setUploadPreview(null);
                      }}
                      className="text-sm text-[#231F20] hover:underline"
                    >
                      Andere afbeelding kiezen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="text-gray-600">
                      <span className="font-semibold text-[#231F20]">Klik om te uploaden</span> of sleep je afbeelding hier
                    </p>
                    <p className="text-sm text-gray-500">PNG, JPG, WebP, HEIC tot 50MB</p>
                  </div>
                )}
              </div>

              <input
                id="file-upload"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />

              {selectedFile && (
                <button
                  onClick={handleUploadImage}
                  disabled={isUploading}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? 'Uploaden...' : 'Gebruik deze afbeelding'}
                </button>
              )}
            </div>
          )}
        </div>
        </div>
      </main>
    </div>
  );

  async function handleFileSelect(file: File) {
    if (file.size > 50 * 1024 * 1024) {
      alert('Bestand is te groot. Maximale grootte is 50MB.');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  async function handleUploadImage() {
    if (!selectedFile || !activeSession || !activeUser) return;

    try {
      setIsUploading(true);

      const { compressImage } = await import('../lib/imageCompression');
      const { uploadToStorage } = await import('../lib/storageUpload');
      const { saveAndNavigate } = await import('../lib/navigationHelpers');

      const compressed = await compressImage(selectedFile, 1920, 0.5, 'webp', 0.85);

      const result = await uploadToStorage(
        compressed.blob,
        activeUser.id,
        'wizard',
        { sessionId: activeSession.id }
      );

      console.log('Image uploaded successfully:', result.publicUrl);

      if (!isMountedRef.current) return;
      await saveAndNavigate(
        activeSession.id,
        {
          selected_image_url: result.publicUrl,
          ...(activeSession.state === 'completed' ? {} : { state: 'image_selected' }),
        },
        ['selected_image_url'],
        navigate,
        wizardUrl(WIZARD_PATHS.preview, activeSession.id)
      );
    } catch (error) {
      console.error('Upload error:', error);
      alert('Er ging iets mis bij het uploaden. Probeer het opnieuw.');
      setIsUploading(false);
    }
  }
}
